/**
 * PositionSelect — combobox должности с подсказкой из глобального справочника
 * (p2-position-dictionary, Волна 3). Заменяет свободный ввод `SuggestInput kind="position"`
 * и `datalist COMMON_POSITIONS`.
 *
 * Все формы кабинета используют режим `allowCreate` (position-free-text: должность —
 * ТЕКСТОВОЕ поле, вариантов наименований бесконечно много, а справочник лишь ускоряет
 * заполнение — выбор по первым буквам): свободный ввод разрешён, текст не сбрасывается на blur,
 * новая должность заводится позже вместе с сотрудником/рабочим местом/должностью анкеты
 * (см. докстринги пропа `allowCreate` и `onChange`). Без `allowCreate` компонент работает в
 * строгом режиме (только выбор существующей записи, при потере фокуса без выбора поле
 * возвращается к последнему валидному значению) — режим оставлен для полей, где выбор из
 * справочника действительно обязателен.
 *
 * Клавиатура (a11y, WAI-ARIA combobox): ↑/↓ двигают активную опцию (с прокруткой в вид),
 * Enter коммитит активную, Esc закрывает список; `aria-activedescendant`/`aria-controls`/
 * `role="option"` связывают инпут и опции. Строгий commit — только через выбор (мышь/Enter),
 * не через blur-таймер.
 *
 * Подсказки (positions-dictionary-review, positions-hide): в списке — только ПРОВЕРЕННЫЕ и не СКРЫТЫЕ
 * администратором должности (непроверенные — свежий ввод клиентов с возможными опечатками — и скрытые
 * опечатки, уже попавшие в заказы, другим клиентам не предлагаются);
 * точное совпадение введённого текста и отображение выбранного значения ищутся по всему
 * справочнику. Справочник большой (≈ 7 тыс. записей после загрузки архива СОУТ), поэтому варианты
 * ранжируются: сначала начинающиеся с введённого, затем где введённое — начало слова, затем
 * остальные; внутри группы — порядок справочника (по алфавиту).
 *
 * Регистр и опечатки (names-and-positions-normalization): в режиме свободного ввода на уходе с поля
 * текст приводится к виду «Слесарь-ремонтник» (`normalizePositionName`, зеркало серверного правила);
 * если проверенной должности с таким написанием нет, а есть очень похожая — под полем подсказка
 * «Возможно, вы имели в виду: …» (`suggestSimilarPositions`), клик подставляет вариант. Браузер
 * подчёркивает опечатки (`spellCheck`, `lang="ru"` — в Firefox проверка включается только явно).
 *
 * Значение — `positionId` существующей записи (+ отображаемое имя). Для мест хранения, где
 * лежит только текст-снапшот должности (условия рабочего места СОУТ, подписанты), компонент
 * принимает `valueName` как фолбэк отображения и резолвит id по имени из загруженного списка.
 *
 * Выпадающий список рендерится через ПОРТАЛ в `document.body` (спека `dialog-portal-layering`,
 * образец `SuggestInput`): внутри `DialogContent` с `overflow-y-auto` список у нижнего края
 * не обрезается границей overflow предка. Портал позиционируется `position:fixed` относительно
 * инпута и слоится на `z-popover` (выше диалога, `pointer-events-auto` — Radix Dialog ставит
 * `pointer-events:none` на body). Служебный сентинел «(Должность не указана)» (и любые записи
 * с именем на «(») скрыты из списка. При ошибке загрузки — отличимое сообщение + «Повторить».
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useId } from 'react'
import { createPortal } from 'react-dom'
import { isOfferedPosition, positionsApi, type Position } from '@/lib/api'
import { Input } from '@/design-system/components/primitives/input'
import { useComboboxEscape } from './use-combobox-escape'
import { useWorkScaleClass } from '@/design-system/work-scale'
import { cn } from '@/lib/utils'
import { normalizePositionName } from '@/lib/name-case'
import { suggestSimilarPositions } from '@/lib/position-suggest'

/** Максимум опций в списке — при превышении показываем хинт «уточните запрос». */
const MAX_OPTIONS = 50

/**
 * Модульный кэш справочника должностей: список небольшой (~100–600) и глобальный, поэтому
 * грузим один раз на сессию и переиспользуем во всех combobox'ах. `pending` дедуплицирует
 * параллельные первые запросы (несколько PositionSelect на одном экране).
 */
let positionsCache: Position[] | null = null
let pendingFetch: Promise<Position[]> | null = null

/** Сбрасывает модульный кэш справочника — общая реализация для теста и прод-инвалидации. */
function resetPositionsCache(): void {
  positionsCache = null
  pendingFetch = null
}

/** Сброс модульного кэша справочника — для изоляции тестов (vitest). */
export function __resetPositionsCacheForTests(): void {
  resetPositionsCache()
}

/**
 * Публичная инвалидация модульного кэша справочника должностей — вызывается после
 * create/rename/delete в admin-справочнике (`pages/positions.tsx`), чтобы открытые в текущей
 * сессии формы клиента (`PositionSelect`) увидели изменения при следующем открытии/фокусе
 * (кэш иначе живёт всю сессию, см. комментарий к `positionsCache` выше).
 */
export function invalidatePositionsCache(): void {
  resetPositionsCache()
}

/**
 * Варианты подсказки для введённого текста: только проверенные и не скрытые должности, содержащие запрос (без
 * учёта регистра, «ё» = «е»), по группам — начинается с запроса, запрос с начала слова, остальные;
 * внутри группы порядок исходного списка сохраняется. Пустой запрос — все предлагаемые.
 */
export function rankPositionSuggestions(positions: Position[], query: string): Position[] {
  const q = normalizeForSearch(query.trim())
  const offered = positions.filter(isOfferedPosition)
  if (!q) return offered
  const groups: Position[][] = [[], [], []]
  for (const p of offered) {
    const name = normalizeForSearch(p.name)
    let tier = -1
    for (let at = name.indexOf(q); at >= 0; at = name.indexOf(q, at + 1)) {
      if (at === 0) {
        tier = 0
        break
      }
      if (WORD_SEPARATOR.test(name[at - 1])) tier = 1
      else if (tier < 0) tier = 2
    }
    if (tier >= 0) groups[tier].push(p)
  }
  return groups.flat()
}

/** Разделитель слов в наименовании должности (начало слова — после него). */
const WORD_SEPARATOR = /[\s\-(«»"/,.]/

/** Поиск без учёта регистра и различия «ё/е». */
function normalizeForSearch(s: string): string {
  return s.toLowerCase().replaceAll('ё', 'е')
}

/** Записи-сентинелы («(Должность не указана)» и пр.) скрыты из выбора — префикс «(». */
function isSentinel(name: string): boolean {
  return name.trimStart().startsWith('(')
}

function loadPositions(): Promise<Position[]> {
  if (positionsCache) return Promise.resolve(positionsCache)
  if (pendingFetch) return pendingFetch
  pendingFetch = positionsApi
    .list()
    .then((items) => {
      positionsCache = items.filter((p) => !isSentinel(p.name))
      return positionsCache
    })
    .finally(() => {
      pendingFetch = null
    })
  return pendingFetch
}

interface UsePositions {
  positions: Position[]
  loading: boolean
  error: boolean
  /** Повторная загрузка справочника после ошибки (кэш на ошибке не заполняется). */
  reload: () => void
}

/** Подгружает и кэширует справочник должностей (без сентинелов). */
export function usePositions(): UsePositions {
  const [positions, setPositions] = useState<Position[]>(positionsCache ?? [])
  const [loading, setLoading] = useState(positionsCache === null)
  const [error, setError] = useState(false)
  const aliveRef = useRef(true)

  const load = useCallback(() => {
    if (positionsCache) {
      setPositions(positionsCache)
      setLoading(false)
      setError(false)
      return
    }
    setLoading(true)
    setError(false)
    loadPositions()
      .then((items) => {
        if (aliveRef.current) {
          setPositions(items)
          setLoading(false)
        }
      })
      .catch(() => {
        if (aliveRef.current) {
          setError(true)
          setLoading(false)
        }
      })
  }, [])

  useEffect(() => {
    aliveRef.current = true
    if (!positionsCache) load()
    return () => {
      aliveRef.current = false
    }
  }, [load])

  return { positions, loading, error, reload: load }
}

/**
 * Единый placeholder полей должности, допускающих ручной ввод или выбор из списка
 * (centrika-docs!96, common.md «Поля должности»). Формы собственный placeholder не задают —
 * текст один на все такие поля.
 */
export const POSITION_FREE_INPUT_PLACEHOLDER =
  'Начните вводить должность. Можно ввести вручную или выбрать из списка'

/** Placeholder строгого режима (выбор только из справочника) — требованием !96 не затронут. */
export const POSITION_STRICT_PLACEHOLDER = 'Начните вводить должность'

export interface PositionSelectProps {
  /** Выбранный `positionId` (авторитетный ключ). */
  value?: string
  /**
   * Текст-фолбэк отображения для мест хранения без positionId (снапшоты условий/подписантов):
   * если `value` пуст, показывается `valueName`; выбор всё равно строгий (только из справочника).
   */
  valueName?: string
  /**
   * Выбор/ввод должности. При строгом режиме — `positionId` существующей записи + имя. В режиме
   * `allowCreate` (centrika-docs!62) при свободном вводе нового имени приходит `positionId=''`
   * и введённый текст; при точном совпадении (trim, без регистра) с записью справочника —
   * её `positionId` (автоподстановка существующей).
   */
  onChange: (positionId: string, name: string) => void
  /**
   * Разрешить ввод НОВОЙ должности (centrika-docs!62, issue #6): поле принимает свободный текст
   * (не сбрасывается на blur), новая должность создаётся ПОЗЖЕ вместе с сотрудником или рабочим
   * местом. Совпадение с существующей подставляется автоматически. По умолчанию (строгий режим) —
   * только выбор из справочника.
   */
  allowCreate?: boolean
  id?: string
  placeholder?: string
  required?: boolean
  invalid?: boolean
  disabled?: boolean
  'aria-invalid'?: boolean | undefined
  'aria-describedby'?: string | undefined
  /** Обязательность для вспомогательных технологий: маркер «*» у подписи от них скрыт. */
  'aria-required'?: true | undefined
}

export function PositionSelect({
  value,
  valueName,
  onChange,
  allowCreate,
  id,
  placeholder,
  required,
  invalid,
  disabled,
  ...aria
}: PositionSelectProps) {
  const { positions, loading, error, reload } = usePositions()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Активная опция для клавиатурной навигации (-1 — ничего не подсвечено).
  const [active, setActive] = useState(-1)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const reactId = useId()
  const listboxId = `${reactId}-listbox`
  const optionId = (i: number) => `${reactId}-opt-${i}`

  useEffect(() => () => clearTimeout(blurTimer.current ?? undefined), [])

  // Отображаемое имя выбранной должности: по positionId из справочника, иначе текст-фолбэк.
  const selected = value ? positions.find((p) => p.id === value) : undefined
  const display = selected?.name ?? valueName ?? ''

  // Все совпадения (для хинта усечения) и усечённый видимый список — только проверенные, по рангу.
  const matches = useMemo(() => rankPositionSuggestions(positions, query), [positions, query])
  const filtered = useMemo(() => matches.slice(0, MAX_OPTIONS), [matches])
  const truncated = matches.length > filtered.length

  // Сброс подсветки при смене запроса/открытия — активная опция не «залипает» вне диапазона.
  useEffect(() => {
    setActive(-1)
  }, [query, open])

  // В открытом состоянии инпут показывает вводимый фильтр; в закрытом — выбранное имя.
  const inputValue = open ? query : display
  const visible = open && !disabled

  // «Возможно, вы имели в виду…» — только для свободного ввода, по текущему тексту поля.
  const suggestions = useMemo(
    () => (allowCreate && !disabled && !loading ? suggestSimilarPositions(inputValue, positions) : []),
    [allowCreate, disabled, loading, inputValue, positions],
  )

  useLayoutEffect(() => {
    if (!visible) return
    const update = () => {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [visible])

  // Прокрутка активной опции в видимую область при навигации с клавиатуры.
  useEffect(() => {
    if (!visible || active < 0) return
    const el = document.getElementById(optionId(active))
    // scrollIntoView отсутствует в jsdom — вызываем опционально.
    el?.scrollIntoView?.({ block: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, visible])

  function close() {
    setOpen(false)
    setQuery('')
    setActive(-1)
  }

  // Escape закрывает только список, а не модальный диалог вокруг поля (`AddWorkplaceForm`
  // внутри `AddToStructureModal`/`WorkplacesCard`) — см. докстринг хука.
  // Шкала шрифтов портального списка (centrika-docs!31): класс берётся из контекста, а не
  // ставится безусловно — портал живёт в `document.body`, вне DOM-поддерева рабочей области.
  const workScale = useWorkScaleClass()
  useComboboxEscape(visible, anchorRef, close)

  function choose(p: Position) {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    onChange(p.id, p.name)
    setQuery('')
    setActive(-1)
    setOpen(false)
  }

  // Свободный ввод (allowCreate, centrika-docs!62): значение уходит наверх сразу; при точном
  // совпадении (trim, без регистра) с записью справочника подставляется её id (автоподстановка
  // существующей), иначе id пуст — новая должность создастся вместе с сотрудником.
  function emitFreeText(text: string) {
    const norm = text.trim().toLowerCase()
    const match = norm ? positions.find((p) => p.name.trim().toLowerCase() === norm) : undefined
    onChange(match?.id ?? '', text)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (loading) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          return
        }
        if (filtered.length > 0) {
          setActive((a) => (a < 0 ? 0 : Math.min(a + 1, filtered.length - 1)))
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open && filtered.length > 0) {
          setActive((a) => (a <= 0 ? 0 : a - 1))
        }
        break
      case 'Enter':
        // Клавиатурный commit: только при открытом списке и выбранной активной опции —
        // иначе не перехватываем Enter (не мешаем сабмиту формы, если список закрыт).
        if (open && active >= 0 && active < filtered.length) {
          e.preventDefault()
          choose(filtered[active])
        }
        break
      // Escape здесь НЕ обрабатывается: он перехватывается раньше, в фазе захвата на window
      // (`useComboboxEscape`) — иначе модальный Radix Dialog закрылся бы первым.
      default:
        break
    }
  }

  const activeDescendant = visible && active >= 0 ? optionId(active) : undefined
  // Свободный ввод и строгий выбор различаются подсказкой внутри поля (centrika-docs!96).
  const effectivePlaceholder =
    placeholder ?? (allowCreate ? POSITION_FREE_INPUT_PLACEHOLDER : POSITION_STRICT_PLACEHOLDER)

  return (
    <div ref={anchorRef}>
      <Input
        id={id}
        value={inputValue}
        placeholder={loading ? 'Загрузка справочника…' : effectivePlaceholder}
        required={required}
        invalid={invalid}
        // Инпут НЕ блокируется на время загрузки (иначе фокус теряется, blur закрывает список
        // и ломает ретрай/навигацию) — состояние загрузки/ошибки показывается внутри списка.
        disabled={disabled}
        aria-invalid={aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        aria-required={aria['aria-required']}
        aria-busy={loading || undefined}
        autoComplete="off"
        // Подчёркивание опечаток браузером — только в свободном вводе (lang — для Firefox).
        spellCheck={allowCreate ? true : undefined}
        lang={allowCreate ? 'ru' : undefined}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={visible ? listboxId : undefined}
        aria-activedescendant={activeDescendant}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
          // allowCreate: свободный текст сразу применяется наверх (иначе теряется на blur).
          if (allowCreate) emitFreeText(e.target.value)
        }}
        onFocus={(e) => {
          // Инициализируем query текущим значением и выделяем его — при Tab-проходе выбранное
          // значение не «исчезает», а ввод сразу заменяет выделенный текст.
          setQuery(display)
          e.currentTarget.select()
          setOpen(true)
        }}
        onBlur={() => {
          // Свободный ввод: на уходе с поля текст приводится к виду «Слесарь-ремонтник» — клиент
          // видит итог до сохранения (сервер нормализует и сам).
          // Текст поля — запрос при открытом списке, иначе показанное значение (список могли
          // закрыть клавишей Escape до ухода с поля).
          // Запись справочника не трогаем: её имя каноническое (сервер подставит его же).
          if (allowCreate) {
            const current = open ? query : display
            const norm = current.trim().toLowerCase()
            const known = norm !== '' && positions.some((p) => p.name.trim().toLowerCase() === norm)
            const normalized = normalizePositionName(current)
            if (!known && normalized !== current) {
              if (open) setQuery(normalized)
              emitFreeText(normalized)
            }
          }
          // Строгий выбор: partial-ввод отбрасывается (закрытие вернёт display выбранного).
          // allowCreate: введённый текст уже применён наверх (emitFreeText) и остаётся в поле
          // (display = valueName), поэтому просто закрываем список, ничего не теряя.
          blurTimer.current = setTimeout(() => {
            setOpen(false)
            setQuery('')
            setActive(-1)
          }, 150)
        }}
      />
      {suggestions.length > 0 ? (
        <p className="mt-1 text-13 text-txt-secondary" aria-live="polite">
          Возможно, вы имели в виду:{' '}
          {suggestions.map((p, i) => (
            <span key={p.id}>
              {i > 0 ? ', ' : null}
              <button
                type="button"
                className="text-accent-txt underline underline-offset-2 hover:no-underline"
                // Фокус остаётся в поле: blur не закрывает и не нормализует до выбора варианта.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(p)}
              >
                {p.name}
              </button>
            </span>
          ))}
        </p>
      ) : null}
      {visible && pos
        ? createPortal(
            <ul
              id={listboxId}
              className={cn(
                'ds-surface pointer-events-auto fixed z-popover max-h-56 overflow-auto rounded-8 py-1 shadow-elevated',
                workScale,
              )}
              // pointerEvents inline (не только Tailwind-класс): модальный Radix Dialog ставит
              // pointer-events:none на <body>, портал-потомок его наследует; inline-стиль
              // перебивает наследование там, где класс не применён (в т.ч. jsdom-тесты).
              style={{ top: pos.top, left: pos.left, width: pos.width, pointerEvents: 'auto' }}
              role="listbox"
            >
              {loading ? (
                <li className="px-3 py-2 text-13 text-txt-tertiary" aria-live="polite">
                  Загрузка справочника…
                </li>
              ) : error ? (
                <li
                  className="flex flex-col gap-2 px-3 py-2 text-13 text-txt-secondary"
                  role="alert"
                >
                  <span className="text-red">Не удалось загрузить справочник должностей.</span>
                  <button
                    type="button"
                    className="self-start rounded-7 border border-line-strong px-2.5 py-1 text-13 text-txt hover:bg-hover"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => reload()}
                  >
                    Повторить
                  </button>
                </li>
              ) : filtered.length === 0 ? (
                <li className="px-3 py-2 text-13 text-txt-tertiary">
                  {allowCreate && query.trim()
                    ? `Новая должность «${query.trim()}» — будет добавлена при сохранении`
                    : query.trim()
                      ? 'Должность не найдена в справочнике'
                      : 'Справочник должностей пуст'}
                </li>
              ) : (
                <>
                  {filtered.map((p, i) => (
                    <li key={p.id} id={optionId(i)} role="option" aria-selected={p.id === value}>
                      <button
                        type="button"
                        className={
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-14 text-txt hover:bg-hover' +
                          (i === active ? ' bg-hover' : '')
                        }
                        // preventDefault на mousedown удерживает фокус в инпуте (портал вне инпута),
                        // чтобы blur-таймер не закрыл список до onClick.
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => choose(p)}
                      >
                        {p.name}
                      </button>
                    </li>
                  ))}
                  {truncated ? (
                    <li
                      className="px-3 py-1.5 text-12 text-txt-tertiary"
                      aria-hidden
                    >{`Показаны первые ${MAX_OPTIONS} — уточните запрос`}</li>
                  ) : null}
                </>
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}
